
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Bookmark, Clock, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ProfileForm from "./ProfileForm";
import SavedItems from "./SavedItems";
import BrowsingHistory from "./BrowsingHistory";
import ProfileDashboard from "./ProfileDashboard";

interface ProfileTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  profile: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
  uploading: boolean;
  autoSaved?: boolean;
  handleImageUpload?: (url: string) => void;
  savingState?: 'idle' | 'saving' | 'saved' | 'error';
  isProfileComplete?: boolean;
}

const ProfileTabs = ({ 
  activeTab, 
  setActiveTab, 
  profile, 
  handleChange, 
  handleSubmit,
  uploading,
  autoSaved,
  handleImageUpload,
  savingState,
  isProfileComplete
}: ProfileTabsProps) => {
  const tabVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <TabsList className="grid grid-cols-4 w-full max-w-md mx-auto bg-muted/50 backdrop-blur-sm">
            <TabsTrigger 
              value="dashboard" 
              className="flex items-center gap-2 transition-all duration-300 hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2"
              >
                <LayoutDashboard size={16} />
                <span className="hidden sm:inline">Dashboard</span>
              </motion.div>
            </TabsTrigger>
            <TabsTrigger 
              value="profile" 
              className="flex items-center gap-2 transition-all duration-300 hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2"
              >
                <User size={16} />
                <span className="hidden sm:inline">Profile</span>
              </motion.div>
            </TabsTrigger>
            <TabsTrigger 
              value="saved" 
              className="flex items-center gap-2 transition-all duration-300 hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2"
              >
                <Bookmark size={16} />
                <span className="hidden sm:inline">Saved</span>
              </motion.div>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="flex items-center gap-2 transition-all duration-300 hover:bg-primary/10 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            >
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2"
              >
                <Clock size={16} />
                <span className="hidden sm:inline">History</span>
              </motion.div>
            </TabsTrigger>
          </TabsList>
        </motion.div>
        
        <AnimatePresence mode="wait">
          <TabsContent value="dashboard" className="mt-0">
            <motion.div
              key="dashboard"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <ProfileDashboard profile={profile} />
            </motion.div>
          </TabsContent>
          
          <TabsContent value="profile" className="mt-0">
            <motion.div
              key="profile"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <ProfileForm 
                profile={profile} 
                handleChange={handleChange} 
                handleSubmit={handleSubmit} 
                uploading={uploading}
                autoSaved={autoSaved}
                handleImageUpload={handleImageUpload}
                savingState={savingState}
                isProfileComplete={isProfileComplete}
              />
            </motion.div>
          </TabsContent>
          
          <TabsContent value="saved" className="mt-0">
            <motion.div
              key="saved"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <SavedItems />
            </motion.div>
          </TabsContent>
          
          <TabsContent value="history" className="mt-0">
            <motion.div
              key="history"
              variants={tabVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <BrowsingHistory />
            </motion.div>
          </TabsContent>
        </AnimatePresence>
      </Tabs>
    </motion.div>
  );
};

export default ProfileTabs;
